// @ts-nocheck
// Team-lead view: the merged agent feed grouped by call group, with
// monitor / whisper / barge on every connected row. Reuses cti2's useSnoop
// when the host passes one (TeamDashboardView) and creates its own
// otherwise (the sidebar page).
import React, { useCallback, useMemo, useState } from 'react';
import { RefreshCw, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/feedback/EmptyState';
import { Dot, EmptyNote, Panel, Pill } from '@/features/cti2/team/teamUi';
import SnoopButtons from '@/features/cti2/team/SnoopButtons';
import { useSnoop } from '@/features/cti2/team/useSnoop';
import { useLiveCalls } from '../useLiveCalls';
import { buildAlerts, countCalls, filterByPhase, groupByCallGroup, toSnoopRow, type PhaseFilter } from '../liveCallsModel';
import LiveCallsKpiStrip from './LiveCallsKpiStrip';
import LiveCallsAlertsBar from './LiveCallsAlertsBar';
import LiveCallsTable from './LiveCallsTable';
import LiveCallDetailSheet from './LiveCallDetailSheet';
import { TransportLabel } from './LiveCallsBoard';

interface Props {
  enabled?: boolean;
  snoop?: any;
  canSnoop?: boolean;
  /** Smaller KPI row + no page chrome, for embedding in the cti2 dashboard. */
  embedded?: boolean;
}

export default function TeamLeadLiveBoard({ enabled = true, snoop: snoopProp, canSnoop = true, embedded = false }: Props) {
  const ownSnoop = useSnoop(!snoopProp && canSnoop);
  const snoop = snoopProp || ownSnoop;
  const [group, setGroup] = useState<number | 'all'>('all');
  const [phase, setPhase] = useState<PhaseFilter>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const query = useMemo(() => ({ callGroupId: group === 'all' ? undefined : group, limit: 200 }), [group]);
  const feed = useLiveCalls(query, enabled);
  const calls = feed.data?.calls || [];
  const groups = feed.data?.scope?.groups || [];

  const counters = useMemo(() => countCalls(calls), [calls]);
  const alerts = useMemo(() => buildAlerts(calls, feed.now), [calls, feed.now]);
  const buckets = useMemo(() => groupByCallGroup(filterByPhase(calls, phase), group === 'all' ? groups : groups.filter((g) => g.callGroupId === group)), [calls, phase, groups, group]);
  const selected = useMemo(() => calls.find((c) => c.id === selectedId) || null, [calls, selectedId]);
  const pick = useCallback((id: string) => setSelectedId(id), []);

  if (feed.error && !feed.data) {
    return <EmptyState icon={Users} title="Live calls unavailable" description={feed.error} action={<Button variant="outline" size="sm" onClick={feed.refresh}>Retry</Button>} />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setGroup('all')}>
            <Pill tone={group === 'all' ? 'call' : 'off'}>All my groups <span className="tabular-nums opacity-70">{calls.length}</span></Pill>
          </button>
          {groups.map((g) => (
            <button key={g.callGroupId} type="button" onClick={() => setGroup(g.callGroupId)}>
              <Pill tone={group === g.callGroupId ? 'call' : 'off'}>
                {g.name} <span className="tabular-nums opacity-70">{calls.filter((c) => c.callGroupIds?.includes(g.callGroupId)).length}</span>
              </Pill>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {snoop.session ? (
            <button type="button" onClick={snoop.stop} disabled={snoop.busy}>
              <Pill tone="crit">{snoop.busy ? 'Stopping…' : `Stop ${snoop.session.mode}`}</Pill>
            </button>
          ) : null}
          <TransportLabel transport={feed.transport} stale={feed.data?.stale} />
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Refresh" onClick={feed.refresh}>
            <RefreshCw className={feed.loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      <LiveCallsKpiStrip counters={counters} counts={null} phase={phase} onPhase={setPhase} compact />

      <Panel className="p-3">
        <LiveCallsAlertsBar alerts={alerts} onPick={pick} />
      </Panel>

      {!feed.loading && !buckets.length ? (
        <Panel className="p-4"><EmptyNote>No live calls in your call groups right now.</EmptyNote></Panel>
      ) : null}

      {buckets.map((b) => (
        <Panel key={b.callGroupId} className="p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Dot tone={b.calls.length ? 'call' : 'off'} />
              <span className="text-[13px] font-semibold">{b.name}</span>
              <span className="text-[11px] text-muted-foreground">
                {b.calls.length} live{b.agentIds.length ? ` · ${b.agentIds.length} agents` : ''}
              </span>
            </div>
          </div>
          <LiveCallsTable
            tableId={`live-calls-tl-${b.callGroupId}`}
            calls={b.calls}
            now={feed.now}
            isLoading={feed.loading}
            onRowClick={(c) => setSelectedId(c.id)}
            snoop={snoop}
            canSnoop={canSnoop}
            pageSize={embedded ? 10 : 25}
            emptyTitle="No live calls in this group"
            emptyDescription="Rows appear when one of these agents is dialled or starts a call."
          />
        </Panel>
      ))}

      <LiveCallDetailSheet
        call={selected}
        now={feed.now}
        open={Boolean(selectedId)}
        onOpenChange={(o) => !o && setSelectedId(null)}
        actions={selected ? <SnoopButtons row={toSnoopRow(selected)} snoop={snoop} allowed={canSnoop} /> : null}
      />
    </div>
  );
}
